import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { evDate } from "@/lib/eventDate";

export const JumpDialog = ({ open, onOpenChange, uid, departure, timelines, entities, onSaved }) => {
  const travellers = entities.filter((e) => ["Character", "Faction"].includes(e.category));
  const [arrTl, setArrTl] = useState(timelines.find((t) => t.id !== departure.timeline_id)?.id || departure.timeline_id);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ arrival_event_id: "", traveller_id: departure.entity_ids?.find((id) => travellers.some((t) => t.id === id)) || travellers[0]?.id || "", description: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/timelines/${arrTl}/events`).then((r) => setEvents(r.data.filter((e) => !e.inherited && e.id !== departure.id)));
    setForm((f) => ({ ...f, arrival_event_id: "" }));
  }, [arrTl, departure.id]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/universes/${uid}/jumps`, { departure_event_id: departure.id, ...form });
      toast.success("Jump recorded");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="jump-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl flex items-center gap-2"><Hourglass className="h-6 w-6 text-[#8B5CF6]" />Add a time-travel jump</DialogTitle>
            <DialogDescription>Departs from <b className="text-[#F3EBDD]">{departure.title}</b> ({evDate(departure)}).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-6">
            <div className="space-y-2">
              <Label>Traveller</Label>
              <Select value={form.traveller_id} onValueChange={(v) => setForm({ ...form, traveller_id: v })}>
                <SelectTrigger data-testid="jump-traveller-select"><SelectValue placeholder="Choose a character or faction" /></SelectTrigger>
                <SelectContent>{travellers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Arrival timeline</Label>
                <Select value={arrTl} onValueChange={setArrTl}>
                  <SelectTrigger data-testid="jump-timeline-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Arrival event</Label>
                <Select value={form.arrival_event_id} onValueChange={(v) => setForm({ ...form, arrival_event_id: v })}>
                  <SelectTrigger data-testid="jump-arrival-select"><SelectValue placeholder={events.length ? "Choose event" : "No events there"} /></SelectTrigger>
                  <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{evDate(e)} · {e.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>How it happened</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Fell through the Mirror Gate" data-testid="jump-description-input" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy || !form.arrival_event_id || !form.traveller_id} data-testid="jump-save-button">{busy ? "Saving…" : "Save jump"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
